import { endOfDay } from "date-fns";
import React, { useEffect, useState } from "react";

import { faCircleInfo } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	FormCheckboxGroup,
	FormField,
	FormMultipleSelect,
	Icon,
	MultipleSelectInput,
	PanelFilters,
	Text,
	Tooltip,
} from "@performyard/py-components";

import { Divider } from "../../../../../components/Divider";
import DatePicker from "../../../../../components/Inputs/DatePicker";

import { type FilterFormSchema, FilterFormSchema as PanelSchema } from "./types";

const ReviewCycleReviewsDetailsTableFilterPanel = (props: {
	isOpen: boolean;
	onClose: () => void;
	queryParams: FilterFormSchema;
	handleQueryChange: (queryParams: Partial<FilterFormSchema>) => void;
	subjectGroupOptions: Parameters<typeof MultipleSelectInput<string>>[0]["options"];
}) => {
	const { subjectGroupOptions, isOpen, onClose, queryParams, handleQueryChange } = props;

	const [pendingFilters, setPendingFilters] = useState<FilterFormSchema>(queryParams);

	// Update local filters when query params change
	useEffect(() => {
		setPendingFilters(queryParams);
	}, [isOpen]);

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={PanelSchema}
			defaultValues={pendingFilters}
			onSubmit={({ value, resetForm, setError }) => {
				handleQueryChange(value);
				resetForm();
				setError("search", "");
				onClose();
			}}
		>
			<div className="space-y-4">
				<FormMultipleSelect<string>
					id="groupIds"
					options={subjectGroupOptions}
					placeholder="Select groups"
					label="Reviews by Group"
				/>
				<FormField
					label="Due Date Before"
					id="dueDateBefore"
					input={(field) => (
						<DatePicker
							hasIcon={true}
							value={field.value as Date}
							onChange={(value) => field.onChange(value ? new Date(value) : null)}
						/>
					)}
				/>
				<FormField
					label="Due Date After"
					id="dueDateAfter"
					input={(field) => (
						<DatePicker
							hasIcon={true}
							value={field.value as Date}
							onChange={(value) => field.onChange(value ? endOfDay(new Date(value)) : null)}
						/>
					)}
				/>
				<Divider />
				<FormCheckboxGroup<FilterFormSchema["unassignedForms"][0]>
					id="unassignedForms"
					options={[
						{ label: "Show only reviews with unassigned forms", value: "some" },
						{ label: "Show only reviews with fully assigned forms", value: "none" },
					]}
					label="Unassigned Forms"
				/>

				<FormCheckboxGroup<FilterFormSchema["externalForms"][0]>
					id="externalForms"
					options={[
						{ label: "Show only reviews with external forms", value: "some" },
						{ label: "Show only reviews without external forms", value: "none" },
					]}
					label="External Forms"
				/>
				<FormCheckboxGroup<FilterFormSchema["awaitingApprovalForms"][0]>
					id="awaitingApprovalForms"
					options={[
						{ label: "Show only reviews with pending approvals", value: "some" },
						{
							label: "Show only reviews without pending approvals",
							value: "none",
						},
					]}
					label="360 Forms"
				/>
				<Divider />
				<FormCheckboxGroup<FilterFormSchema["authorProgress"][0]>
					id="authorProgress"
					options={[
						{ label: "No Progress", value: "none" },
						{
							label: (
								<div className="flex h-[16px] items-start">
									<Text size="paragraph-3" weight="regular">
										Some Progress
									</Text>
									<Tooltip content="Reviews with at least one author task completed.">
										<Icon icon={faCircleInfo} size={12} color="neutral-7" />
									</Tooltip>
								</div>
							),
							value: "some",
						},
						{ label: "Completed", value: "completed" },
					]}
					label="Author Progress"
				/>
				<FormCheckboxGroup<FilterFormSchema["signerProgress"][0]>
					id="signerProgress"
					options={[
						{ label: "No Progress", value: "none" },
						{
							label: (
								<div className="flex h-[16px] items-start">
									<Text size="paragraph-3" weight="regular">
										Some Progress
									</Text>
									<Tooltip content="Reviews with at least one signer task completed.">
										<Icon icon={faCircleInfo} size={12} color="neutral-7" />
									</Tooltip>
								</div>
							),
							value: "some",
						},
						{ label: "Completed", value: "completed" },
					]}
					label="Signer Progress"
				/>
				<FormCheckboxGroup<FilterFormSchema["overallProgress"][0]>
					id="overallProgress"
					options={[
						{ label: "No Progress", value: "none" },
						{
							label: (
								<div className="flex h-[16px] items-start">
									<Text size="paragraph-3" weight="regular">
										Some Progress
									</Text>
									<Tooltip content="Reviews with at least one author or signer task completed.">
										<Icon icon={faCircleInfo} size={12} color="neutral-7" />
									</Tooltip>
								</div>
							),
							value: "some",
						},
						{ label: "Completed", value: "completed" },
					]}
					label="Overall Progress"
				/>
			</div>
		</PanelFilters>
	);
};

export default ReviewCycleReviewsDetailsTableFilterPanel;
